import store from '@/store';
import request from '@/utils/request';
import ms from 'ms';
import sleep from 'sleep-promise';

export class ApiClient {

  public static refreshTokenEnabled: boolean = true;

  public static async autoRefreshToken() {
    this.refreshTokenEnabled = true;
    while (this.refreshTokenEnabled) {
      try {
        if (store && store.state && store.state.user && store.state.user.jwt) {
          const { data } = await new this().refreshToken();
          store.commit('user/setJwt', data.token);
        }
      } catch (e) {
        console.error(e);
      } finally {
        await sleep(ms('12h'));
      }
    }
  }

  // account
  public account() {
    return request({
      url: '/account',
    });
  }

  // accounts
  public login(data: any) {
    return request({
      url: '/accounts/login',
      method: 'post',
      data,
    });
  }

  public refreshToken() {
    return request({
      url: '/accounts/refreshToken',
    });
  }

  public accounts(params: any = {}) {
    return request({
      url: '/accounts',
      params,
    });
  }

  public postAccounts(data: any = {}) {
    return request({
      url: '/accounts',
      method: 'post',
      data,
    });
  }

  public putAccounts(id: number, data: any) {
    return request({
      url: `/accounts/${id}`,
      method: 'put',
      data,
    });
  }

  public deleteAccounts(id: number) {
    return request({
      url: `/accounts/${id}`,
      method: 'delete',
    });
  }

  public forgotPassword(data: any) {
    return request({
      url: `/accounts/forgotPassword`,
      method: 'post',
      data,
    });
  }

  // settings
  public settings(data: any = {}) {
    return request({
      url: '/settings',
      data,
    });
  }

  public putSettings(id: number, data: any = {}) {
    return request({
      url: `/settings/${id}`,
      method: 'put',
      data,
    });
  }

  // vendors
  public vendors(data: any = {}) {
    return request({
      url: '/vendors',
      data,
    });
  }

  public putVendors(id: number, data: any = {}) {
    return request({
      url: `/vendors/${id}`,
      method: 'put',
      data,
    });
  }

  // regions
  public regions() {
    return request({
      url: '/regions',
    });
  }

  // services
  public services() {
    return request({
      url: '/services',
    });
  }

  // customers
  public customers(params: any = {}) {
    return request({
      url: '/customers',
      params,
    });
  }

  public putCustomers(id: number, data: any = {}) {
    return request({
      url: `/customers/${id}`,
      method: 'put',
      data,
    });
  }

  public postCustomers(data: any = {}) {
    return request({
      url: '/customers',
      method: 'post',
      data,
    });
  }

  public postCustomerResetSmsLimiterCount(id: number) {
    return request({
      url: `/customers/resetSmsLimiterCount`,
      method: 'post',
      data: {
        id
      }
    });
  }

  public postCustomerResetWhatsappLimiterCount(id: number) {
    return request({
      url: `/customers/resetWhatsappLimiterCount`,
      method: 'post',
      data: {
        id
      }
    });
  }

  public customersExport() {
    return request({
      url: `/customers/export`,
      params: {},
      responseType: 'blob',
    });
  }

  // dashboard
  public customersDashboardsSummary(params: any = {}) {
    return request({
      url: `/customers/dashboards/summary`,
      params
    });
  }


  public customerDashboardsSummary(id: number, params: any = {}) {
    return request({
      url: `/customers/dashboards/${id}/summary`,
      params,
    });
  }

  public customersDashboardsApplications(id: number, params: any = {}) {
    return request({
      url: `/customers/dashboards/${id}/applications`,
      params,
    });
  }


  // report
  public customersReportTopUp(id: number, params: any = {}) {
    return request({
      url: `/customers/${id}/report/topUp`,
      params,
    });
  }

  public customersReportSms(id: number, params: any = {}) {
    return request({
      url: `/customers/${id}/report/sms`,
      params,
    });
  }

  public customersReportWhatsApp(id: number, params: any = {}) {
    return request({
      url: `/customers/${id}/report/whatsApp`,
      params,
    });
  }


  public customersReportExportSpreadsheet(id: number, params: any = {}) {
    return request({
      url: `/customers/${id}/report/export/spreadsheet`,
      params,
      responseType: 'blob',
    });
  }

  public customersReportExportPdf(id: number, params: any = {}) {
    return request({
      url: `/customers/${id}/report/export/pdf`,
      params,
      responseType: 'blob',
    });
  }

  public customersReportEmail(id: number, data: any = {}) {
    return request({
      url: `/customers/${id}/report/email`,
      method: 'post',
      data,
    });
  }

  // wallets
  public wallets(params: any = {}) {
    return request({
      url: '/wallets',
      params,
    });
  }

  public walletsTransaction(id: number, data: any = {}) {
    return request({
      url: `/wallets/${id}/transaction`,
      method: 'post',
      data,
    });
  }

  public postWallet(data: any = {}) {
    return request({
      url: '/wallets',
      method: 'post',
      data,
    });
  }

  public putWallet(id: number, data: any = {}) {
    return request({
      url: `/wallets/${id}`,
      method: 'put',
      data,
    });
  }

  // transactions
  public transactions(params: any = {}) {
    return request({
      url: '/transactions',
      params,
    });
  }

  // messages
  public communicationMessages(params: any = {}) {
    return request({
      url: '/communicationMessages',
      params,
    });
  }

  public communicationMessagesExport(params: any = {}) {
    return request({
      url: '/communicationMessages/export',
      params,
      responseType: 'blob',
    });
  }

  public communicationMessagesStates() {
    return request({
      url: '/communicationMessages/states',
    });
  }

  // cm
  public cmWhatsappOutgoingMessages(params: any = {}) {
    return request({
      url: '/cmWhatsappOutgoingMessages',
      params,
    });
  }

  public cmWhatsappIncomingMessages(params: any = {}) {
    return request({
      url: '/cmWhatsappIncomingMessages',
      params,
    });
  }

  // ycloud
  public ycloudWhatsappOutgoingMessages(params: any = {}) {
    return request({
      url: '/ycloudWhatsappOutgoingMessages',
      params,
    });
  }

  public ycloudWhatsappIncomingMessages(params: any = {}) {
    return request({
      url: '/ycloudWhatsappIncomingMessages',
      params,
    });
  }

  public postYcloudWhatsappTemplate(data: any = {}) {
    return request({
      method: 'post',
      url: '/ycloud/whatsapp/template/list',
      data,
    });
  }

  public deleteYcloudWhatsappTemplate(data: any = {}) {
    return request({
      method: "delete",
      url: '/ycloud/whatsapp/template',
      data,
    });
  }

  // twilio
  public twilioWhatsAppChats(params: any = {}) {
    return request({
      url: '/twilioWhatsAppChats',
      params,
    });
  }

  public twilioWhatsAppChatsExport(params: any = {}) {
    return request({
      url: '/twilioWhatsAppChats/export',
      params,
    });
  }

  public twilioWhatsAppChatsStates() {
    return request({
      url: '/twilioWhatsAppChats/states',
    });
  }

  //
  public applicationTwilioWhatsAppWebhooks(params: any = {}) {
    return request({
      url: '/applicationTwilioWhatsAppWebhooks',
      params,
    });
  }

  // applications
  public applications(params: any = {}) {
    return request({
      url: `/applications`,
      params,
    });
  }

  public applicationsCreditsSearch(data: any = {}) {
    return request({
      url: `/applications/sms/credits/search`,
      method: 'post',
      data,
    });
  }

  public postApplications(data: any = {}) {
    return request({
      url: `/applications`,
      method: 'post',
      data,
    });
  }

  public putApplications(id: number, data: any = {}) {
    return request({
      url: `/applications/${id}`,
      method: 'put',
      data,
    });
  }

  public applicationRenewApiKey(id: number) {
    return request({
      url: `/applications/${id}/renewApiKey`,
      method: 'patch',
    });
  }


  // application message verify
  public applicationsMessageVerifies(params: any = {}) {
    return request({
      url: `/applicationMessageVerifies`,
      params,
    });
  }

  public applicationsMessageVerifiesStates() {
    return request({
      url: `/applicationMessageVerifies/states`,
    });
  }

  public applicationMessageVerifiesMessageVerifyMessagesApprove(id: number, data: any) {
    return request({
      url: `/applicationMessageVerifies/MessageVerifyMessages/${id}/approve`,
      method: 'patch',
      data,
    });
  }

  // action history
  public actionHistories(params: any = {}) {
    return request({
      url: `/actionHistories`,
      params,
    });
  }

  public actionHistoriesEntities() {
    return request({
      url: `/actionHistories/entities`,
    });
  }

  public actionHistoriesActions() {
    return request({
      url: `/actionHistories/actions`,
    });
  }

  // whatsApp
  public whatsAppRateLimitsMetrics(params: any = {}) {
    return request({
      url: `/whatsApps/rateLimits/metrics`,
      params,
    });
  }

  // phones
  public phones(params: any = {}) {
    return request({
      url: `/phones`,
      params,
    });
  }

  public phonesCountryCodes(params: any = {}) {
    return request({
      url: `/phones/countryCodes`,
      params,
    });
  }

  // logs
  public logsRequests(params: any = {}) {
    return request({
      url: `/logs/requests`,
      params,
    });
  }

  public logsRequestsMethods(params: any = {}) {
    return request({
      url: `/logs/requests/methods`,
      params,
    });
  }

  // helpers
  private objectToFormData(object: any) {
    const data = new FormData();
    Object.entries(object).forEach(([key, value]: [string, any]) => data.append(key, value));
    return data;
  }

}

ApiClient.autoRefreshToken();

export const apiClient = new ApiClient();
